import React, { useState, useEffect, useCallback } from 'react';
import Navigation from './components/Navigation';
import MCPToolsPanel from './components/MCPToolsPanel';
import SystemMonitor from './components/SystemMonitor';
import EnhancedDashboard from './components/EnhancedDashboard';
import ModelManager from './components/ModelManager';
import ClientManager from './components/ClientManager';
import VideoAnalysis from './components/VideoAnalysis';
import QueueManager from './components/QueueManager';
import RoutingKernel from './components/RoutingKernel';
import AgentChat from './components/AgentChat';
import SelfRepair from './components/SelfRepair';
import TerminalActivityLogs from './components/TerminalActivityLogs';
import ApiKeyLogin from './components/ApiKeyLogin';
import { TelemetryDashboard } from './components/TelemetryDashboard';

import TelegramMobileApp from './components/TelegramMobileApp';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';

import { OmniAdminPanel } from './components/OmniAdminPanel';
import { LiveVisionTerminal } from './components/LiveVisionTerminal';
import { AgentManagementPanel } from './components/AgentManagementPanel';
import AgentModePanel from './components/AgentModePanel';
import { SkillBuilderPanel } from './components/SkillBuilderPanel';
import { LiveLogsPanel } from './components/LiveLogsPanel';
import { MediaOutputsPanel } from './components/MediaOutputsPanel';
import AceStepPanel from './components/AceStepPanel';
import BridgePanel from './components/BridgePanel';
import BrowserAgentView from './components/BrowserAgentView';

import { AgentOrchestrationProvider } from './context/AgentOrchestrationContext';
import { DraggableDashboard } from './components/DraggableDashboard';
import { QueueItem, ModelConfig, APIClient, SystemLog, KernelSettings, SystemStats } from './types';
import { backendService } from './services/backendService';
import { aiService } from './services/aiService';
import { errorHandler } from './services/errorHandler';
import { StatePreservation } from './services/statePreservation';
import { initMobileStorage } from './utils/mobileStorage';

// Keys that are obviously placeholder/invalid — force re-login
const INVALID_PLACEHOLDER_KEYS = new Set([
  'admin-initial-key',
  'admin-init-key',
  'nexus-admin-2026',
  '',
]);

const getStoredApiKey = (): string | null => {
  const key = localStorage.getItem('omnigate_api_key');
  if (!key || INVALID_PLACEHOLDER_KEYS.has(key)) return null;
  return key;
};

const detectAudit1200Mode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const audit = params.get('audit');
  const layout = params.get('layout');
  return audit === '1200' || audit === '1' || layout === 'audit1200';
};

const App: React.FC = () => {
  const { isTelegram, webApp } = useTelegramWebApp();

  // ─── Auth gate ───────────────────────────────────────────────────────────
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return getStoredApiKey() !== null;
  });

  // ─── Handle Google OAuth redirect ──────────────────────────────────────
  useEffect(() => {
    // Check if we returned from Google OAuth with api_key in hash fragment
    if (typeof window === 'undefined') return;

    const hash = window.location.hash;
    if (!hash || !hash.startsWith('#google-auth')) return;

    const params = new URLSearchParams(hash.replace('#google-auth?', ''));
    const apiKeyFromGoogle = params.get('api_key');
    const email = params.get('email') || '';
    const name = params.get('name') || '';

    if (apiKeyFromGoogle) {
      console.log('[App] Google OAuth login successful:', email, name);

      // Clean hash from URL
      window.history.replaceState(null, '', window.location.pathname + window.location.search);

      // Store API key
      localStorage.setItem('omnigate_api_key', apiKeyFromGoogle);
      localStorage.setItem('omnigate_admin_key', apiKeyFromGoogle);
      localStorage.setItem('omnigate_google_email', email);
      localStorage.setItem('omnigate_google_name', name);

      // Also save to Capacitor for mobile
      import('./utils/mobileStorage').then(({ setItem }) => {
        setItem('omnigate_api_key', apiKeyFromGoogle).catch(() => {});
        setItem('omnigate_admin_key', apiKeyFromGoogle).catch(() => {});
      }).catch((e) => {
        console.warn('[App] Failed to save to mobile storage:', e);
      });

      setIsAuthenticated(true);
    }
  }, []);

  const handleAuthenticated = (apiKey: string) => {
    // Patch backendService defaults so it uses the new key without reload
    localStorage.setItem('omnigate_api_key', apiKey);
    localStorage.setItem('omnigate_admin_key', apiKey);
    setIsAuthenticated(true);
  };

  // ─── Main app state ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = StatePreservation.getActiveTab();
    return savedTab || 'dashboard';
  });
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [isMainChatActive, setIsMainChatActive] = useState(false);
  const _appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const [kernel, setKernel] = useState<KernelSettings>({
    localPort: 8888,
    externalTunnelActive: true,
    tunnelProvider: 'TELEBIT',
    externalUrl: _appOrigin + '/',
    encryptionEnabled: true,
    autoConvertTTS: true,
    modelAliasing: false,
    crossProviderTokens: true,
    watchDuration: 'INDEFINITE',
    visionFps: 1,
    audioSamplingInterval: 5,
    audioBurstDuration: 1.5,
    aiConfig: {
      activeProvider: 'CUSTOM',
      vision: { type: 'CUSTOM', modelId: 'qwen3-vl:4b', baseUrl: _appOrigin },
      asr: { type: 'CUSTOM', modelId: 'faster-whisper-api', baseUrl: _appOrigin },
      tts: { type: 'CUSTOM', modelId: 'tts-1', voiceId: 'goscia_clean', baseUrl: _appOrigin },
      agent: { type: 'CUSTOM', modelId: 'qwen3-vl:4b', baseUrl: _appOrigin }
    }
  });

  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [clients, setClients] = useState<APIClient[]>([]);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [stats, setStats] = useState<SystemStats>({
    cpuLoad: 0, gpuLoad: 0, vramUsed: 0, vramTotal: 24, ramUsed: 0, ramTotal: 64, activeRequests: 0, throughput: 0
  });
  const [viewportWidth, setViewportWidth] = useState<number>(() => (typeof window !== 'undefined' ? window.innerWidth : 1920));
  const [isAudit1200Mode, setIsAudit1200Mode] = useState<boolean>(() => detectAudit1200Mode());
  const [isChatFocusMode, setIsChatFocusMode] = useState<boolean>(false);

  const isCloudAvailable = true;

  const addLog = useCallback((level: SystemLog['level'], message: string, component?: string) => {
    const newLog: SystemLog = {
      id: `L-${Math.random().toString(36).substr(2, 5).toUpperCase()}`,
      timestamp: new Date().toLocaleTimeString(),
      level,
      message,
      component
    };
    setLogs(prev => [newLog, ...prev].slice(0, 100));
  }, []);

  useEffect(() => {
    aiService.updateConfig(kernel.aiConfig);

    // Initialize mobile storage on app start
    initMobileStorage().then(() => {
      addLog('INFO', 'Mobile storage initialized', 'System');
    }).catch(err => {
      console.warn('[App] Mobile storage init failed:', err);
    });

    errorHandler.setupGlobalHandlers();

    // Add initial system logs
    addLog('INFO', 'OMNIGATE System Initialized', 'Kernel');
    addLog('INFO', 'Architect Neuronowy ready for commands', 'Architect');

    const unsubscribe = errorHandler.subscribe((error) => {
      const systemLog = errorHandler.toSystemLog(error);
      setLogs(prev => [systemLog, ...prev].slice(0, 100));
    });

    return () => {
      unsubscribe();
    };
  }, [kernel.aiConfig, addLog]);

  // Hydrate clients from backend on mount (after auth)
  useEffect(() => {
    if (!isAuthenticated) return;
    backendService.fetchClients().then(loaded => {
      if (loaded && loaded.length > 0) {
        setClients(loaded);
        addLog('INFO', `Loaded ${loaded.length} API clients from backend`, 'Access');
      }
    }).catch(() => {
      addLog('WARN', 'Could not load API clients from backend', 'Access');
    });
  }, [isAuthenticated, addLog]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    const handlePopState = () => setIsAudit1200Mode(detectAudit1200Mode());

    handleResize();
    handlePopState();

    window.addEventListener('resize', handleResize);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('audit-1200', isAudit1200Mode);
    return () => document.body.classList.remove('audit-1200');
  }, [isAudit1200Mode]);

  useEffect(() => {
    if (activeTab !== 'dashboard' && isChatFocusMode) {
      setIsChatFocusMode(false);
    }
  }, [activeTab, isChatFocusMode]);

  useEffect(() => {
    let isMounted = true;
    const initialize = async () => {
      try {
        const [fetchedModels, fetchedClients] = await Promise.all([
          backendService.fetchModels(),
          backendService.fetchClients()
        ]);
        if (!isMounted) return;
        setModels(fetchedModels);
        setClients(fetchedClients);
        backendService.connectStats((backendData) => {
          if (!isMounted || !backendData) return;
          setStats(prev => ({
            ...prev,
            cpuLoad: backendData.system?.cpu_percent ?? prev.cpuLoad,
            gpuLoad: backendData.gpu?.load_percent ?? prev.gpuLoad,
            vramUsed: backendData.gpu?.vram_used_mb ? (backendData.gpu.vram_used_mb / 1024) : prev.vramUsed,
            vramTotal: backendData.gpu?.vram_total_mb ? (backendData.gpu.vram_total_mb / 1024) : prev.vramTotal,
            activeRequests: backendData.active_connections ?? prev.activeRequests,
            throughput: backendData.requests?.total_tokens ?? prev.throughput
          }));
        });
      } catch (err) {
        console.warn("App initialization error:", err);
      }
    };
    initialize();
    return () => { isMounted = false; };
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const handleAgentTool = useCallback((call: any) => {
    addLog('AI_ACTION', `Agentic Execute: ${call.name}`, 'Kernel');
  }, [addLog]);

  const compactNavigation = isAudit1200Mode || viewportWidth <= 1280;

  useEffect(() => {
    StatePreservation.saveTabState(activeTab, {
      activeTab,
      logFilters: {
        level: 'ALL',
        searchTerm: '',
        autoScroll: true
      }
    });
  }, [activeTab]);

  const renderContent = () => {
    switch (activeTab) {
      case 'models': return <ModelManager models={models} setModels={setModels} />;
      case 'settings': return <RoutingKernel kernel={kernel} setKernel={setKernel} />;
      case 'admin-omni': return (
        <div className="flex flex-col lg:flex-row h-full gap-4 min-h-0">
          <div className="w-full lg:w-1/2 overflow-y-auto">
            <OmniAdminPanel />
          </div>
          <div className="w-full lg:w-1/2 overflow-y-auto">
            <LiveVisionTerminal />
          </div>
        </div>
      );
      case 'agents': return (
        <div className="flex flex-col h-full gap-4 min-h-0 p-0">
          <AgentModePanel />
        </div>
      );
      case 'skills': return <SkillBuilderPanel />;
      case 'media': return <MediaOutputsPanel />;
      case 'acestep': return <AceStepPanel />;
      case 'logs': return <LiveLogsPanel />;
      case 'clients': return <ClientManager clients={clients} setClients={setClients} />;
      case 'mcp': return <MCPToolsPanel />;
      case 'browser': return <BrowserAgentView />;
      case 'bridge': return <BridgePanel />;
      case 'telemetry': return <TelemetryDashboard />;
      default: return null;
    }
  };

  // ─── Auth gate render ────────────────────────────────────────────────────
  if (isTelegram) {
    return (
      <AgentOrchestrationProvider>
        <TelegramMobileApp
           webApp={webApp}
           isAuthenticated={isAuthenticated}
           onAuthenticated={handleAuthenticated}
           kernel={kernel}
           setKernel={setKernel}
           stats={stats}
           setStats={setStats}
           addLog={addLog}
           queue={queue}
           setQueue={setQueue}
           logs={logs}
           clearLogs={clearLogs}
           handleAgentTool={handleAgentTool}
           setIsMainChatActive={setIsMainChatActive}
           isAudit1200Mode={isAudit1200Mode}
           isChatFocusMode={isChatFocusMode}
        />
      </AgentOrchestrationProvider>
    );
  }

  if (!isAuthenticated) {
    return <ApiKeyLogin onAuthenticated={handleAuthenticated} />;
  }

  return (
    <AgentOrchestrationProvider>
      <div className="flex min-h-screen bg-[#131722] text-[#d1d4dc] selection:bg-purple-500/30">
        <Navigation activeTab={activeTab} setActiveTab={setActiveTab} gpuLoad={stats.gpuLoad} compact={compactNavigation} />
        <main className="flex-1 overflow-y-auto overflow-x-hidden relative">

          {/* FIX: DASHBOARD JEST ZAWSZE ZAMONTOWANY W DRZEWIE DOM - Utrzymuje stan czatu i agenta przy zmianie zakładek */}
          <div style={{ display: activeTab === 'dashboard' ? 'flex' : 'none' }} className="flex-col min-h-screen bg-[#131722] text-[#d1d4dc] absolute inset-0 z-10">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-[#2a2e39] p-3 bg-[#131722]">
              <div className="flex justify-between items-end">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-black text-white tracking-tighter uppercase italic">Terminal <span className="text-purple-500">Master</span></h1>
                    {isCloudAvailable ? (
                      <span className="text-[7px] bg-green-500/10 text-green-500 border border-green-500/20 px-1 py-0.5 rounded font-black uppercase tracking-widest">Hybrid Link</span>
                    ) : (
                      <span className="text-[7px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1 py-0.5 rounded font-black uppercase tracking-widest">Local Mode</span>
                    )}
                  </div>
                  <p className="text-[7px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                    Gateway: {kernel.localPort} • Infrastructure: {isCloudAvailable ? 'GEMINI+LOCAL' : 'PURE LOCAL'}
                  </p>
                </div>
                <div className="flex gap-3 font-mono">
                  {isAudit1200Mode && (
                    <button
                      onClick={() => setIsChatFocusMode(prev => !prev)}
                      className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded border border-purple-500/40 text-purple-300 hover:bg-purple-500/15 transition-colors"
                      title="Przełącz fokus na panel Architekta Neuronowego"
                    >
                      {isChatFocusMode ? 'Show Full Dashboard' : 'Chat Focus'}
                    </button>
                  )}

                  <div className="text-right">
                    <p className="text-[6px] font-bold text-slate-600 uppercase">Requests</p>
                    <p className="text-sm font-black text-yellow-500 leading-none tracking-tighter">{stats.activeRequests}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[6px] font-bold text-slate-600 uppercase">Flow</p>
                    <p className="text-sm font-black text-orange-500 leading-none tracking-tighter">{stats.throughput} tk</p>
                  </div>
                </div>
              </div>
              {isAudit1200Mode && (
                <p className="mt-2 text-[8px] font-bold uppercase tracking-widest text-cyan-400">
                  AUDIT_1200 MODE ACTIVE — layout zoptymalizowany do walidacji w viewport 1200x800
                </p>
              )}
            </div>

            {/* Main Content Area with Scroll */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3" style={{ WebkitOverflowScrolling: 'touch' }}>
              <DraggableDashboard
                stats={stats}
                setStats={setStats}
                addLog={addLog}
                queue={queue}
                setQueue={setQueue}
                logs={logs}
                clearLogs={clearLogs}
                handleAgentTool={handleAgentTool}
                setIsMainChatActive={setIsMainChatActive}
                isAudit1200Mode={isAudit1200Mode}
                isChatFocusMode={isChatFocusMode}
              />
            </div>
          </div>

          {/* BUG-07 FIX: VideoAnalysis zawsze zamontowany */}
          <div style={{ display: activeTab === 'video' ? 'block' : 'none' }} className="h-full overflow-auto p-6 relative z-10 bg-[#131722]">
            <VideoAnalysis settings={kernel} setKernel={setKernel} isMainChatActive={isMainChatActive} />
          </div>

          {/* Blok uruchamia renderContent tylko dla innych zakładek */}
          <div className="h-full relative z-0">
             {activeTab !== 'video' && renderContent()}
          </div>
        </main>
      </div>
    </AgentOrchestrationProvider>
  );
};

export default App;
